import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useDialog } from "../context/DialogProvider";
import { AVAILABLE_FIELDS } from "./RegistrationForm";

const SelectedInputFields = ({ formFields = [], onDataSubmit }) => {
  const { closeDialog } = useDialog();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedPhone, setSelectedPhone] = useState(() => {
    const existingPhone = formFields.find((f) => f.isPhoneField);
    return existingPhone ? existingPhone.id : "";
  });

  const [selectedEmail, setSelectedEmail] = useState(() => {
    const existingEmail = formFields.find((f) => f.isEmailField);
    return existingEmail ? existingEmail.id : "";
  });

  const handleSubmit = () => {
    onDataSubmit({
      phoneFieldId: selectedPhone,
      emailFieldId: selectedEmail,
    });
    closeDialog();
  };

  const getIconByType = (type) => {
    const fieldDef = AVAILABLE_FIELDS.find((f) => f.type === type);
    return fieldDef ? fieldDef.icon : null;
  };

  // ✅ Correct: useCallback returns a function that can take arguments
  const getSelectedLabel = useCallback(
    (selectedId) => {
      const field = formFields.find((f) => f.id === selectedId);
      return field ? field.properties.label : "";
    },
    [formFields]
  );

  return (
    <Box
      p={4}
      display="flex"
      flexDirection="column"
      gap={3}
      sx={{
        width: isMobile ? "100%" : 600,
        mx: "auto",
      }}
    >
      <Typography variant="h5">
        {formFields.length} fields you have selected
      </Typography>

      {/* Removed scroll restrictions; added flex-wrap layout */}
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          p: 1.5,
        }}
      >
        <List
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            p: 0,
          }}
        >
          {formFields.map((field) => (
            <ListItem
              key={field.id}
              sx={{
                width: "auto", // Shrink to fit content
                py: 0.5,
                px: 1,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                transition:
                  "background-color 0.15s ease, border-color 0.15s ease",
                "&:hover": {
                  bgcolor: "action.hover",
                  borderColor: "primary.light",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: "auto", // Remove default minWidth
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: "action.selected",
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 1,
                }}
              >
                {/* Clone element to force a smaller icon size if needed */}
                {React.cloneElement(getIconByType(field.type), { size: 16 })}
              </ListItemIcon>
              <ListItemText
                primary={field.properties.label}
                secondary={field.type}
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: 13,
                  lineHeight: 1.2,
                }}
                secondaryTypographyProps={{ fontSize: 10, lineHeight: 1.2 }}
                sx={{ m: 0 }} // Remove default margins for compactness
              />
            </ListItem>
          ))}
          {formFields.length === 0 && (
            <Typography variant="body2" color="text.secondary" p={1}>
              No fields selected.
            </Typography>
          )}
        </List>
      </Box>

      <Divider />

      <Typography variant="subtitle1" fontWeight="bold">
        Select which field is <code>phone</code> and and which is{" "}
        <code>email</code>
      </Typography>

      <FormControl fullWidth size="small">
        <InputLabel id="phone-select-label">Phone Number Field</InputLabel>
        <Select
          labelId="phone-select-label"
          value={selectedPhone}
          label="Phone Number Field"
          onChange={(e) => setSelectedPhone(e.target.value)}
          renderValue={getSelectedLabel}
          sx={{ borderRadius: 1.5 }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {formFields.map((field) => (
            <MenuItem
              key={field.id}
              value={field.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                "&.Mui-selected": {
                  bgcolor: "action.selected",
                },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: "action.hover",
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(getIconByType(field.type), { size: 16 })}
              </Box>
              <Typography variant="body2">{field.properties.label}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth size="small">
        <InputLabel id="email-select-label">Email Field</InputLabel>
        <Select
          labelId="email-select-label"
          value={selectedEmail}
          label="Email Field"
          onChange={(e) => setSelectedEmail(e.target.value)}
          renderValue={getSelectedLabel}
          sx={{ borderRadius: 1.5 }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {formFields.map((field) => (
            <MenuItem
              key={field.id}
              value={field.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                py: 1,
                "&.Mui-selected": {
                  bgcolor: "action.selected",
                },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: "action.hover",
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(getIconByType(field.type), { size: 16 })}
              </Box>
              <Typography variant="body2">{field.properties.label}</Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={1}>
        <Button onClick={closeDialog} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={!selectedPhone || !selectedEmail}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default SelectedInputFields;
